import { useState } from "react";
import { Link } from "react-router-dom";
import { Form } from "antd";
import { useNavigate } from "react-router";

import styles from "./style.module.scss";

import VLButton from "views/uikit/Buttons/VLButton";
import VLInputWithLabel from "../../../components/VLInputWithLabel";
import { ERROR_MESSAGES } from "./useLoginForm";
import { validateEmail } from "helpers/validations";

const STEPS = {
  GET_STARTED: "GET_STARTED",
  CHANGE_PASSWORD: "CHANGE_PASSWORD",
} as const;

type StepType = (typeof STEPS)[keyof typeof STEPS];

interface GetStartedProps {
  onSuccess: () => void;
}

const GetStarted = ({ onSuccess }: GetStartedProps) => {
  const [form] = Form.useForm();

  const handleSubmit = async () => {
    try {
      onSuccess();
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className={styles.formSection}>
      <div className={styles.formHeader}>
        <div className={styles.formTitle}>Get started</div>
        <div className={styles.formDescription}>
          Sign in to enjoy the feature of Visual Layer
        </div>
      </div>

      <Form
        form={form}
        id="onprem-login-form"
        data-form-id="onprem-login-form"
        className={styles.form}
        onFinish={handleSubmit}
        layout="vertical"
        requiredMark={false}
      >
        <Form.Item shouldUpdate noStyle>
          {() => (
            <div className={styles.formInputGroup}>
              <Form.Item
                className={styles.formItem}
                name="username"
                rules={[
                  { required: true, message: ERROR_MESSAGES.required },
                  {
                    validator(_, value) {
                      if (!value || validateEmail(value)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(ERROR_MESSAGES.invalidEmail),
                      );
                    },
                  },
                ]}
              >
                <VLInputWithLabel
                  id="username"
                  type="text"
                  label="User Name"
                  error={
                    form.getFieldError("username")[0] &&
                    form.isFieldTouched("username")
                      ? form.getFieldError("username")[0]
                      : null
                  }
                />
              </Form.Item>

              <Form.Item
                className={styles.formItem}
                name="password"
                rules={[{ required: true, message: ERROR_MESSAGES.required }]}
              >
                <VLInputWithLabel
                  id="password"
                  type="password"
                  label="Password"
                  error={
                    form.getFieldError("password")[0] &&
                    form.isFieldTouched("password")
                      ? form.getFieldError("password")[0]
                      : null
                  }
                  showPasswordToggle={true}
                />
              </Form.Item>
            </div>
          )}
        </Form.Item>

        <VLButton
          text="Sign in"
          type="primary"
          htmlType="submit"
          data-testid="onprem-login-submit-button"
          className={styles.submitButton}
        />

        <span className={styles.signInText}>
          Forgot your password? Please contact your workspace admin.
        </span>
      </Form>

      <span className={styles.agreeTerms}>
        By signing in you agree to the terms and{" "}
        <Link
          className={styles.noUnderline}
          to={"https://visual-layer2.readme.io/docs/terms-of-service"}
          target="_blank"
        >
          conditions
        </Link>{" "}
        and the{" "}
        <Link
          className={styles.noUnderline}
          to={"https://visual-layer2.readme.io/docs/privacy-policy"}
          target="_blank"
        >
          privacy policy
        </Link>
      </span>
    </div>
  );
};

interface ChangePasswordProps {
  onSuccess: () => void;
}

const ChangePassword = ({ onSuccess }: ChangePasswordProps) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const handleSubmit = async () => {
    try {
      const success = true;

      if (success) {
        onSuccess();
        navigate("/login");
        return;
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className={styles.formSection}>
      <div className={styles.formHeader}>
        <div className={styles.formTitle}>Change Your password</div>
        <div className={styles.formDescription}>
          Please enter a new password to access your account.
        </div>
      </div>

      <Form
        form={form}
        id="change-password-form"
        data-form-id="change-password-form"
        className={styles.form}
        onFinish={handleSubmit}
        layout="vertical"
        requiredMark={false}
      >
        <Form.Item shouldUpdate noStyle>
          {() => (
            <div className={styles.formInputGroup}>
              <Form.Item
                className={styles.formItem}
                name="newPassword"
                rules={[
                  { required: true, message: ERROR_MESSAGES.required },
                  {
                    min: 8,
                    message: ERROR_MESSAGES.passwordMinLength,
                  },
                ]}
              >
                <VLInputWithLabel
                  id="newPassword"
                  type="password"
                  label="New Password"
                  error={
                    form.getFieldError("newPassword")[0] &&
                    form.isFieldTouched("newPassword")
                      ? form.getFieldError("newPassword")[0]
                      : null
                  }
                  showPasswordToggle={true}
                />
              </Form.Item>

              <Form.Item
                className={styles.formItem}
                name="confirmPassword"
                dependencies={["newPassword"]}
                rules={[
                  { required: true, message: ERROR_MESSAGES.required },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("newPassword") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(ERROR_MESSAGES.passwordMismatch),
                      );
                    },
                  }),
                ]}
              >
                <VLInputWithLabel
                  id="confirmPassword"
                  type="password"
                  label="Confirm new password"
                  error={
                    form.getFieldError("confirmPassword")[0] &&
                    form.isFieldTouched("confirmPassword")
                      ? form.getFieldError("confirmPassword")[0]
                      : null
                  }
                  showPasswordToggle={true}
                />
              </Form.Item>
            </div>
          )}
        </Form.Item>

        <VLButton
          text="Save & Continue"
          type="primary"
          htmlType="submit"
          data-testid="change-password-submit-button"
          className={styles.submitButton}
        />
      </Form>
    </div>
  );
};

const OnPremLogin = () => {
  const [currentStep, setCurrentStep] = useState<StepType>(STEPS.GET_STARTED);

  const stepConfig = {
    [STEPS.GET_STARTED]: (
      <GetStarted onSuccess={() => setCurrentStep(STEPS.CHANGE_PASSWORD)} />
    ),
    [STEPS.CHANGE_PASSWORD]: (
      <ChangePassword onSuccess={() => setCurrentStep(STEPS.GET_STARTED)} />
    ),
  };

  return stepConfig[currentStep];
};

export default OnPremLogin;
